/**
 * MetricsEditModal.tsx — a reusable modal for editing a list of computed
 * metrics (name + id + expression per row).
 *
 * Each row reuses the name/id pattern: a name input with a trailing "ID"
 * addon (click → toggles an id edit input below with an "ID" label + check
 * save button), plus a CEL expression field with live eval. The last row is
 * a "+" placeholder button that calls onAdd to append a new metric.
 *
 * Used by NodeModal (Node Metrics → "+"). Designed to be reusable — the
 * host owns the metrics array and the mutation callbacks; this modal is a
 * pure editor over that array.
 */
import { useState } from 'react';
import { Plus, Trash, Check } from '@phosphor-icons/react';

import type { ComputedMetric } from '../engine/types';
import { Modal } from './Modal';
import { ExprField } from './ExprField';

interface MetricsEditModalProps {
  /** Optional modal title; defaults to "Metrics". */
  title?: string;
  metrics: ComputedMetric[];
  /** Scope for the live expression evaluator (self.*, nodes.*, edges.*). */
  scope: Record<string, unknown>;
  onClose: () => void;
  onAdd: () => void;
  onUpdate: (idx: number, patch: Partial<ComputedMetric>) => void;
  onRemove: (idx: number) => void;
  onRenameKey: (idx: number, newKey: string) => boolean;
  /** Returns true if `key` is taken by another metric (excluding idx). */
  keyTaken: (key: string, idx: number) => boolean;
}

export function MetricsEditModal({
  title = 'Metrics',
  metrics,
  scope,
  onClose,
  onAdd,
  onUpdate,
  onRemove,
  onRenameKey,
  keyTaken,
}: MetricsEditModalProps): JSX.Element {
  return (
    <Modal title={title} onClose={onClose}>
      <section className="modal-section">
        <div className="metrics-list">
          {metrics.map((m, idx) => (
            <MetricEditRow
              key={idx}
              metric={m}
              scope={scope}
              taken={keyTaken(m.key, idx)}
              onUpdate={(patch) => onUpdate(idx, patch)}
              onRemove={() => onRemove(idx)}
              onRenameKey={(nk) => onRenameKey(idx, nk)}
            />
          ))}
          {/* Empty metric slot — always last in the list. It's a placeholder
              for the NEXT metric: same shape as a metric row, dashed, with a
              faint "+". Clicking it appends a real metric in place. */}
          <button type="button" className="metric-empty" onClick={onAdd} title="Add metric">
            <Plus size={16} weight="bold" />
            <span>metric</span>
          </button>
        </div>
      </section>
    </Modal>
  );
}

/**
 * MetricEditRow — name input + trailing "ID" addon (same pattern as the node
 * name/id field) + a CEL expression field below + remove button.
 */
function MetricEditRow({
  metric,
  scope,
  taken,
  onUpdate,
  onRemove,
  onRenameKey,
}: {
  metric: ComputedMetric;
  scope: Record<string, unknown>;
  taken: boolean;
  onUpdate: (patch: Partial<ComputedMetric>) => void;
  onRemove: () => void;
  onRenameKey: (newKey: string) => boolean;
}): JSX.Element {
  const [editingId, setEditingId] = useState(false);
  const [idDraft, setIdDraft] = useState(metric.key);
  const commitId = () => {
    setEditingId(false);
    if (idDraft !== metric.key && idDraft.length > 0 && !taken) {
      if (!onRenameKey(idDraft)) setIdDraft(metric.key);
    } else {
      setIdDraft(metric.key);
    }
  };
  return (
    <div className="metric-edit-item">
      <div className="node-name-group">
        <input
          type="text"
          className="text-input node-name-input"
          placeholder="Metric name"
          value={metric.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
        />
        <button
          type="button"
          className="node-id-addon"
          title="ID"
          onClick={() => { setEditingId(true); setIdDraft(metric.key); }}
        >
          ID
        </button>
        <button type="button" className="input-ext-btn" title="Remove metric" onClick={onRemove}>
          <Trash size={14} />
        </button>
      </div>
      {editingId && (
        <div className="node-id-edit">
          <span className="node-id-edit-label">ID</span>
          <input
            type="text"
            className={`text-input node-id-input${taken ? ' invalid' : ''}`}
            value={idDraft}
            onChange={(e) => setIdDraft(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitId();
              if (e.key === 'Escape') { setIdDraft(metric.key); setEditingId(false); }
            }}
          />
          <button
            type="button"
            className="node-id-save"
            title="Save ID"
            disabled={taken || idDraft.length === 0}
            onClick={commitId}
          >
            <Check size={14} weight="bold" />
          </button>
          {taken && <span className="id-hint">already in use</span>}
        </div>
      )}
      <ExprField
        label="Expression"
        value={metric.expr}
        scope={scope}
        onChange={(expr) => onUpdate({ expr })}
        placeholder="e.g. self.stock * 2"
        multiLine
      />
    </div>
  );
}
